import { listCollection, readJson } from "../lib/content.server.js";
import { buildMeta } from "../lib/seo.js";
import { SITE } from "../lib/site.js";
import { pickWall } from "../lib/wall.js";
import Hero from "../components/Hero/Hero.jsx";
import LogoWall from "../components/LogoWall/LogoWall.jsx";
import ScanStory from "../components/ScanStory/ScanStory.jsx";
import ProductTabs from "../components/ProductTabs/ProductTabs.jsx";
import AnalysesList from "../components/AnalysesList/AnalysesList.jsx";
import ScrollArrows from "../components/ScrollArrows/ScrollArrows.jsx";
import { ANALYSES } from "../lib/analyses.js";
import { surfaceFor } from "../lib/surface.js";
import EntryCard from "../components/EntryCard/EntryCard.jsx";
import TestimonialCarousel from "../components/TestimonialCarousel/TestimonialCarousel.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import CaseStudyShowcase from "../components/CaseStudyShowcase/CaseStudyShowcase.jsx";
import styles from "./home.module.css";

const PAGE = {
  title: "RDV Systems | Road Safety Audit 3D and 3D Visualization",
  description:
    "Road Safety Audit 3D finds sight-distance failures crash data can't. 3D Visualization turns any design into a 3D model anyone can explore in a browser. Since 2005.",
  path: "/",
};

export function meta() {
  return buildMeta(PAGE);
}

export function links() {
  return [
    {
      rel: "preload",
      as: "image",
      href: "/images/site/hero/reel-start-1600.jpg",
      imageSrcSet:
        "/images/site/hero/reel-start-960.webp 960w, /images/site/hero/reel-start-1600.webp 1600w",
      imageSizes: "100vw",
      fetchPriority: "high",
    },
  ];
}

export async function loader() {
  const caseStudies = listCollection("case-studies")
    .filter((e) => e.featured)
    .slice(0, 3);
  const posts = listCollection("blog")
    .slice(0, 3)
    .map((e) => ({ ...e, kind: "post", href: `/blog/${e.slug}` }));
  const webinars = listCollection("webinars")
    .slice(0, 3)
    .map((e) => ({ ...e, kind: "webinar", href: "/resources/webinars" }));
  const latest = [...posts, ...webinars].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 3);
  const clients = readJson("clients");
  // All of them: the home rail runs in a loop, so it is not a three-card shortlist.
  const testimonials = [...readJson("testimonials")].sort(
    (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
  );
  const dots = clients.filter((c) => c.type === "dot").length;
  const wall = pickWall(clients, 32);
  return {
    caseStudies,
    latest,
    wall,
    testimonials,
    stats: { dots, others: clients.length - dots },
  };
}

const PRODUCTS = [
  {
    key: "rsa3d",
    name: "Road Safety Audit 3D",
    title: "Find hazards before construction, not after crashes",
    text: "A 3D safety audit of new designs and existing roads that does not wait for crash history.",
    points: [
      "Fix sight-distance and roadside problems while the design can still change",
      "Rank the riskiest spots on existing roads, so safety funds go where they prevent crashes",
      "Defensible findings against AASHTO and state DOT criteria, as reports, video and GIS layers",
    ],
    href: "/road-safety-audit-3d",
    cta: "Explore Road Safety Audit 3D",
    video: "/videos/rsa-loop.mp4",
    poster: "/images/site/home/rsa-loop.jpg",
    caption: "Sight distance checked on a LiDAR scan of an existing road",
    // The footage's own speed / sight-distance readout sits on its left edge.
    focus: "left center",
  },
  {
    key: "visualization",
    name: "3D Visualization",
    title: "Let everyone see the project before it is built",
    text: "We turn your design files into a photorealistic, accurate 3D model that anyone can explore from a link, on an ordinary laptop, with nothing to install.",
    points: [
      "Build public and stakeholder support with a design people can actually see",
      "Made from your own DGN, DWG, OpenRoads or Civil 3D files, so it is the real geometry",
      "Drive, walk or fly it in any light, weather or traffic, then share videos, stills and tours",
    ],
    href: "/3d-visualization",
    cta: "Explore 3D Visualization",
    video: "/videos/visualization-loop.mp4",
    poster: "/images/site/home/visualization-loop.jpg",
    caption: "An intersection design, rendered as a 3D model for a public meeting",
  },
];

// The one switch for the light sections (redesign step 3, 2026-09-24): these sit on the
// light "plan sheet" surface from app/styles/surfaces.css. Empty the list to put the whole
// page back on the dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["products", "analyses", "testimonials"];
const surface = surfaceFor(LIGHT_SECTIONS);

// The 123BIMLive story over the live scan. Three steps switch at 1/4 and 3/4 of the scroll,
// which is where ScanScene's default story numbers start the analysis and meet the crest.
const STORY_STEPS = [
  {
    title: "Send the scan or the design",
    text: "The LiDAR scan of a road you already manage, or the CAD design you're still working on. It all runs in the cloud.",
  },
  {
    title: "Every station gets checked",
    text: "Sight lines are drawn station by station along the whole corridor and checked against AASHTO, not sampled.",
  },
  {
    title: "Each failure names its cause",
    text: "A crest, trees, a guardrail or a sign, so crews know exactly what to fix, and the findings line up with your crash records.",
  },
];

const entry = (item) => ({
  kind: item.kind,
  href: item.href,
  image: item.image,
  imageAlt: item.imageAlt,
  title: item.title,
  description: item.description,
  meta: item.date,
});

export default function Home({ loaderData }) {
  const { caseStudies, latest, wall, testimonials, stats } = loaderData;
  return (
    <>
      <Hero
        title="See the crash before it happens."
        lead="Road Safety Audit 3D finds the sight-distance failures crash data can't, and 3D Visualization lets everyone see the design."
        primary={{ to: "/demo", label: "Request a demo" }}
        secondary={{ to: "/123bimlive", label: "Explore 123BIMLive" }}
        // The reel's own first frame (3.6s), so the still fades straight into the video; it
        // is also what reduced-motion visitors see. The earlier poster, henderson, is still
        // in public/images/site/hero (the about page uses it).
        poster="/images/site/hero/reel-start"
        // Owner asked for the background loop at Checkpoint 2 (2026-09-17), same as the
        // Elementor background video on the old site.
        // The 2026-09-26 reel (the 1080p export), cut after its logo sting at 3.6s, 24fps,
        // no audio: 1080p for tablets and up, 720p for phones. The previous loop is still at
        // /videos/hero.mp4: set video={{ mp4: "/videos/hero.mp4" }} to revert.
        video={{ mp4: "/videos/hero-reel-720.mp4", mp4Wide: "/videos/hero-reel-1080.mp4" }}
        // The credibility figures sit in the first screen, not down with the case studies:
        // marketing feedback, 2026-09-24.
        stats={[
          { value: `Since ${SITE.foundedYear}`, label: "Road safety and visualization" },
          { value: String(stats.dots), label: "State DOTs" },
          { value: `${stats.others}+`, label: "Agencies, cities and firms" },
        ]}
      />

      <section className="section section-alt" aria-labelledby="trust-title">
        <div className="container">
          <SectionHeader
            id="trust-title"
            center
            title="Trusted by state DOTs, agencies and engineering firms"
          />
          <LogoWall clients={wall} />
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("products")}
        aria-labelledby="products-title"
      >
        <div className="container">
          <SectionHeader
            id="products-title"
            title="Safer roads and designs everyone understands"
            aside="Road Safety Audit 3D finds hazards before crashes do. 3D Visualization lets everyone see a project before it is built."
          />
          <ProductTabs products={PRODUCTS} label="Our products" />
        </div>
      </section>

      {/* Full-bleed and three screens tall: the scan pins while its steps scroll past. */}
      <section aria-labelledby="bimlive-home-title">
        <ScanStory
          titleId="bimlive-home-title"
          kicker="In 123BIMLive"
          title="Upload a LiDAR scan or a CAD design. Get a road safety audit."
          steps={STORY_STEPS}
          action={{ to: "/123bimlive", label: "Explore 123BIMLive" }}
          note="The scene is a simulation running the real AASHTO stopping-sight-distance rule for a 55 mph road."
        />
      </section>

      <section
        className="section"
        data-surface={surface("analyses")}
        aria-labelledby="analyses-home-title"
      >
        <div className="container">
          <SectionHeader
            id="analyses-home-title"
            title="Ten analyses from one scan"
            aside="Every analysis runs on the same scan, so adding one never means collecting data again."
            link={{ to: "/123bimlive#analyses", label: "See every analysis" }}
            controls={
              <ScrollArrows
                target="analyses-cards"
                label="analyses"
                className={styles.analysesArrows}
              />
            }
          />
          <AnalysesList id="analyses-cards" items={ANALYSES} />
        </div>
      </section>

      <section className="section" aria-labelledby="proof-title">
        <div className="container">
          <SectionHeader
            id="proof-title"
            title="Results for DOTs and agencies"
            aside="From an audit of Utah's existing roads to SCDOT's first public meeting in 3D."
            link={{ to: "/case-studies", label: "All case studies" }}
          />
          <CaseStudyShowcase items={caseStudies} />
        </div>
      </section>

      <section
        className="section"
        data-surface={surface("testimonials")}
        aria-labelledby="testimonials-title"
      >
        <div className="container">
          <SectionHeader
            id="testimonials-title"
            title="What DOT leaders and engineers say"
            aside="From executive directors to the engineers who ran the public meetings."
            link={{ to: "/about/testimonials", label: "All testimonials" }}
          />
          <TestimonialCarousel items={testimonials} label="What DOT leaders and engineers say" />
        </div>
      </section>

      <section className="section" aria-labelledby="latest-title">
        <div className="container">
          <SectionHeader
            id="latest-title"
            title="Latest from RDV Systems"
            link={{ to: "/resources", label: "All resources" }}
          />
          <div className={styles.latest}>
            {latest.slice(0, 1).map((item) => (
              <EntryCard key={`${item.kind}-${item.slug}`} variant="feature" {...entry(item)} />
            ))}
            <div className={styles.latestRest}>
              {latest.slice(1).map((item) => (
                <EntryCard key={`${item.kind}-${item.slug}`} variant="compact" {...entry(item)} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/123bimlive/lidar-intersection"
        title="Send us one corridor. We'll show you what the crash data can't."
        text="A LiDAR scan of a road you already manage, or a design you're still working on."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
